import { useState } from 'react';
import { useIntl } from 'react-intl';
import { ButtonUI, Input } from 'sonata-react-components';

import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { getVenuesByIds } from '@/resources/audienceTargeting.resources';
import { SelectedList } from '../components/SelectedList';
import { VenueVisitsStyled } from './VenueVisits.styles';
import { VenueVisitsModal } from './VenueVisitsModal';

type VenueSettings = string[];

type Props = {
  onChange: (itemsIds: VenueSettings) => void;
  onRadiusChange: (radius: number) => void;
};

type State = {
  deletedItems: string[];
  selectedItems: Record<string, any>[];
  showModal: boolean;
};

function VenueVisits({ onChange, onRadiusChange }: Props) {
  const { formatMessage } = useIntl();
  const token: Record<string, any> = {};
  const { user } = useTargetingContext();

  const [{ deletedItems, selectedItems, showModal }, setState] = useState<State>({
    deletedItems: [],
    selectedItems: [],
    showModal: false
  });

  const handleModal = () => {
    setState(prevState => ({
      ...prevState,
      showModal: !showModal
    }));
  };

  const handleModalSave = async (items: Record<string, any>[]) => {
    setState(prevState => ({
      ...prevState,
      selectedItems: items
    }));

    const ids = items.map(item => item._id);
    try {
      if (user) {
        const venuesIds = await getVenuesByIds(ids, user._id, token);

        if (!venuesIds || venuesIds.length === 0) {
          throw new Error('Could not get venue ids');
        }

        onChange(ids);
      }
    } catch (e: any) {
      // biome-ignore lint/suspicious/noConsole: Ignore console.log for logging
      console.log(e);
    }
  };

  const handleDeleteItems = async (deletedItemId: string) => {
    const updatedDeletedItems = [...deletedItems, deletedItemId];
    setState(prevState => ({
      ...prevState,
      deletedItems: updatedDeletedItems
    }));

    const updatedFilteredItemsIds = selectedItems
      .filter(item => !updatedDeletedItems.includes(item._id))
      .map(item => item._id);

    onChange(updatedFilteredItemsIds);
  };

  const handleRadiusChange = (newRadius: string) => {
    onRadiusChange(parseInt(newRadius));
  };

  const filteredItems = selectedItems.filter(item => !deletedItems.includes(item._id));

  const defaultSelectedItems = filteredItems.reduce((acc, item) => {
    return {
      ...acc,
      [item._id]: true
    };
  }, {});

  return (
    <VenueVisitsStyled className="venueVisits" showButton={filteredItems.length === 0}>
      <div className="venueVisits__container">
        {filteredItems.length === 0 ? (
          <div className="venueVisits__content">
            <ButtonUI
              className="venueVisits__button"
              id="venueVisits-button"
              onClick={handleModal}
              size="medium"
              text={formatMessage({
                defaultMessage: 'Select Venues',
                id: 'targeting.audienceTargeting.audienceProfile.venueVisits.buttonSelectVenues'
              })}
              type="primary"
            />
          </div>
        ) : (
          <div className="venueVisits__itemsContainer">
            <div className="venueVisits__content">
              <SelectedList
                items={filteredItems}
                onDeleteItems={handleDeleteItems}
                onModalOpen={handleModal}
                title={formatMessage({
                  defaultMessage: 'Venue List',
                  id: 'targeting.audienceTargeting.audienceProfile.venueVisits.list.title'
                })}
              />
            </div>
            <Input
              className="venueVisits__input"
              disabled={false}
              id="venueVisits-input"
              inputAdditionalProps={{}}
              label={formatMessage({
                defaultMessage: 'Radius',
                id: 'targeting.audienceTargeting.audienceProfile.venueVisits.input.label'
              })}
              name="radiusInput"
              onChange={newValue => handleRadiusChange(newValue as string)}
              placeholder={formatMessage({
                defaultMessage: 'Select Radius',
                id: 'targeting.audienceTargeting.audienceProfile.venueVisits.input.placeholder'
              })}
              required
              shape="squareUI"
              type="text"
            />
          </div>
        )}
      </div>
      {showModal && (
        <VenueVisitsModal
          defaultSelectedItems={defaultSelectedItems}
          isModalOpened={showModal}
          onModalClose={handleModal}
          onModalSave={handleModalSave}
          theme="light"
        />
      )}
    </VenueVisitsStyled>
  );
}

export default VenueVisits;
